import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, createFeature } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, FeatureModel } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import axios from 'axios';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface AddFeatureDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
}

export default function AddFeatureDialog({
  open,
  handleClose,
  changeLogRoles,
}: AddFeatureDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [featureName, setFeatureName] = useState('');
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<FeatureModel>) =>
      createFeature(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Feature'),
      });

      queryClient.setQueryData<FeatureModel[] | undefined>(
        [UserKeys.Features],
        (oldData) => {
          if (oldData) {
            return [...oldData, data];
          }
        }
      );
      setFeatureName('');
      setHasError(false);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('already exists')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Creating('this feature'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.calendars.resource_calendars.create_feature, {
        module: ModuleList.modules.calendars.name,
        section: ModuleList.modules.calendars.sections.resourceCalendars,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.CreateFeatures.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newFeature: Partial<FeatureModel> = { name: featureName };
      mutate(newFeature);
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setFeatureName('');
        setHasError(false);
        handleClose();
      }}
      title="Add Feature"
      dialogContent={
        <Stack>
          <TextField
            autoFocus
            id="feature"
            error={hasError}
            helperText={hasError ? 'Feature already exists.' : ' '}
            label="Name"
            fullWidth
            autoComplete="off"
            value={featureName}
            onChange={(event) => setFeatureName(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const newFeature: Partial<FeatureModel> = { name: featureName };
              mutate(newFeature);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: featureName === '',
        title: 'Add',
      }}
    />
  );
}
